import React from "react";
import { Grid, TextField, Typography } from "@mui/material";
import ReadMoreReadLess from "../../app/common/ReadMoreReadLess";
import { useAppSelector } from "../../app/store/configureStore";
import { AnswersProps } from "../../app/models/Answers";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

const schema = yup.object().shape({
  physical: yup
    .string()
    .required("Must give an answer")
    .max(500, "Max characters reached"),
  cognitive: yup
    .string()
    .required("Must give an answer")
    .max(500, "Max characters reached"),
  communitcation: yup
    .string()
    .required("Must give an answer")
    .max(500, "Max characters reached"),
  creative: yup
    .string()
    .required("Must give an answer")
    .max(500, "Max characters reached"),
});

const CompetenceList: React.FC<AnswersProps> = ({
  answersData,
  onAnswerChange,
}) => {
  const { user } = useAppSelector((state) => state.account);

  const isHomeVisitor = user?.credentialTypeId === 4;
  const physicalSystemCodeId = isHomeVisitor ? 46 : 42;
  const cognitiveSystemCodeId = isHomeVisitor ? 47 : 43;
  const communitcationSystemCodeId = isHomeVisitor ? 48 : 44;
  const creativeSystemCodeId = isHomeVisitor ? 49 : 45;

  const defaultValues = {
    physical: answersData.find(
      (answer) => answer.systemCodeId === physicalSystemCodeId
    )?.answer,
    cognitive: answersData.find(
      (answer) => answer.systemCodeId === cognitiveSystemCodeId
    )?.answer,
    communitcation: answersData.find(
      (answer) => answer.systemCodeId === communitcationSystemCodeId
    )?.answer,
    creative: answersData.find(
      (answer) => answer.systemCodeId === creativeSystemCodeId
    )?.answer,
  };
  const {
    register,
    formState: { errors },
  } = useForm({
    defaultValues,
    resolver: yupResolver(schema),
    mode: "onTouched",
  });

  const renderPhysicalCompetence = () => {
    return (
      <>
        <li>
          How do you incorporate activities to promote gross motor development?
        </li>
        <li>
          How do you ensure health and safety when implementing physical
          activities?
        </li>
        <li>
          How are activities adapted to meet the individual needs of all
          children?
        </li>
        <li>
          What resources and materials do you use each day to assist with fine
          motor skills?
        </li>
      </>
    );
  };
  const renderIntellectualCompetence = () => {
    return (
      <>
        <li>
          What are some activities used daily to support social-emotional
          development?
        </li>
        <li>
          What are some daily activities used to foster cognitive development?
        </li>
        <li>
          What resources and materials are available to children to encourage
          curiosity, exploration, and problem-solving?
        </li>
        <li>
          How do you support the various stages of social-emotional and
          cognitive development?
        </li>
      </>
    );
  };
  const renderCommunicationCompetence = () => {
    return (
      <>
        <li>How do you encourage positive peer interactions?</li>
        <li>
          How are children involved in making decisions that are developmentally
          appropriate?
        </li>
        <li>
          What resources do you use to help children build their communication
          skills?
        </li>
        <li>How do you support verbal and nonverbal communication?</li>
      </>
    );
  };
  const renderCreativeCompetence = () => {
    return (
      <>
        <li>
          How do you create an environment that encourages exploration through
          play?
        </li>
        <li>
          How do you incorporate activities and materials that encourage
          children to develop their imagination?
        </li>
        <li>
          What activities can be included to encourage children to express
          themselves through the arts (music, visual, drama)?
        </li>
      </>
    );
  };

  return (
    <Grid container pb={3}>
      <Grid item xs={12} px={3} pt={3}>
        <Typography variant="body1" fontWeight="bold" paragraph>
          Physical Competence
        </Typography>
        <Typography variant="body1" paragraph>
          {user?.credentialTypeId !== 4
            ? "How do your teaching practices help children develop physical competence? "
            : "How do your teaching practices teach parents to help their children develop physical competence?"}
        </Typography>
        <Grid xs item>
          <ReadMoreReadLess>{renderPhysicalCompetence()}</ReadMoreReadLess>
        </Grid>
      </Grid>
      <Grid item xs={12} px={3} pt={3}>
        <TextField
          label={
            user?.credentialTypeId !== 4
              ? "My teaching practices support the development of physical competence in the following ways:"
              : "My teaching practices help parents to support the development of physical competence in the following ways:"
          }
          multiline
          rows={2}
          {...register("physical")}
          name="physical"
          fullWidth
          value={
            answersData.find(
              (answer) => answer.systemCodeId === physicalSystemCodeId
            )?.answer || ""
          }
          onChange={(e) => {
            const newValue = e.target.value;
            const systemCodeId = physicalSystemCodeId;

            newValue.length <= 500 && onAnswerChange(systemCodeId, newValue);
          }}
          error={!!errors.physical?.message}
          helperText={errors.physical?.message}
        />
        <Typography fontSize={13}>
          {defaultValues.physical?.length}/500
        </Typography>
      </Grid>

      <Grid item xs={12} px={3} pt={3}>
        <Typography variant="body1" fontWeight="bold" paragraph>
          Cognitive Competence
        </Typography>
        <Typography variant="body1" paragraph>
          {user?.credentialTypeId !== 4
            ? "How do your teaching practices help children develop cognitive competence?"
            : "How do your teaching practices teach parents to help their children develop cognitive competence?"}
        </Typography>
        <ReadMoreReadLess>{renderIntellectualCompetence()}</ReadMoreReadLess>
      </Grid>
      <Grid item xs={12} px={3} pt={3}>
        <TextField
          label={
            user?.credentialTypeId !== 4
              ? "My teaching practices support the development of cognitive competence in the following ways:"
              : "My teaching practices help parents to support the development of cognitive competence in the following ways:"
          }
          multiline
          rows={2}
          fullWidth
          {...register("cognitive")}
          name="cognitive"
          value={
            answersData.find(
              (answer) => answer.systemCodeId === cognitiveSystemCodeId
            )?.answer || ""
          }
          onChange={(e) => {
            const newValue = e.target.value;
            const systemCodeId = cognitiveSystemCodeId;

            newValue.length <= 500 && onAnswerChange(systemCodeId, newValue);
          }}
          error={!!errors.cognitive?.message}
          helperText={errors.cognitive?.message}
        />
        <Typography fontSize={13}>
          {defaultValues.cognitive?.length}/500
        </Typography>
      </Grid>

      <Grid item xs={12} px={3} pt={3}>
        <Typography variant="body1" fontWeight="bold" paragraph>
          Creative Competence
        </Typography>
        <Typography variant="body1" paragraph>
          {user?.credentialTypeId !== 4
            ? "How do you help children be creative?"
            : "How do your teaching practices teach parents to help their children be creative?"}
        </Typography>
        <ReadMoreReadLess>
          <>{renderCreativeCompetence()}</>
        </ReadMoreReadLess>
      </Grid>
      <Grid item xs={12} px={3} pt={3}>
        <TextField
          label={
            user?.credentialTypeId !== 4
              ? "My teaching practices support the development of creative competence in the following ways:"
              : "My teaching practices help parents to support the development of creative competence in the following ways:"
          }
          multiline
          rows={2}
          fullWidth
          {...register("creative")}
          name="creative"
          value={
            answersData.find(
              (answer) => answer.systemCodeId === creativeSystemCodeId
            )?.answer || ""
          }
          onChange={(e) => {
            const newValue = e.target.value;
            const systemCodeId = creativeSystemCodeId;

            newValue.length <= 500 && onAnswerChange(systemCodeId, newValue);
          }}
          error={!!errors.creative?.message}
          helperText={errors.creative?.message}
        />
        <Typography fontSize={13}>
          {defaultValues.creative?.length}/500
        </Typography>
      </Grid>

      <Grid item xs={12} px={3} pt={3}>
        <Typography variant="body1" fontWeight="bold" paragraph>
          Communication Competence
        </Typography>
        <Typography variant="body1" paragraph>
          {user?.credentialTypeId !== 4
            ? "How do your teaching practices help children develop communication competence?"
            : "How do your teaching practices teach parents to help their children develop communication competence?"}
        </Typography>
        <ReadMoreReadLess>{renderCommunicationCompetence()}</ReadMoreReadLess>
      </Grid>
      <Grid item xs={12} px={3} pt={3}>
        <TextField
          label={
            user?.credentialTypeId !== 4
              ? "My teaching practices support the development of communication competence in the following ways:"
              : "My teaching practices help parents to support the development of communication competence in the following ways:"
          }
          multiline
          rows={2}
          fullWidth
          {...register("communitcation")}
          name="communitcation"
          value={
            answersData.find(
              (answer) => answer.systemCodeId === communitcationSystemCodeId
            )?.answer || ""
          }
          onChange={(e) => {
            const newValue = e.target.value;
            const systemCodeId = communitcationSystemCodeId;

            newValue.length <= 500 && onAnswerChange(systemCodeId, newValue);
          }}
          error={!!errors.communitcation?.message}
          helperText={errors.communitcation?.message}
        />
        <Typography fontSize={13}>
          {defaultValues.communitcation?.length}/500
        </Typography>
      </Grid>
    </Grid>
  );
};

export default CompetenceList;
